import AutorenewIcon from "@mui/icons-material/Autorenew";
import BackupIcon from "@mui/icons-material/Backup";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material";
import BulkUploadProgress from "components/BulkUploadProgress";
import useGlobalContext from "hooks/globalData";
import useTitle from "hooks/useTitle";
import ImportsStepper from "./ImportsStepper";
import { useEffect } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import importsUploadStore, {
  abortBulkImportsUpload,
  importsBulkUploadStore,
  processFileImport,
} from "stores/importsUploadStore";
import { Color } from "types/colors";

const styles = {
  root: {
    minWidth: 275,
  },
  title: {
    fontSize: "1.25rem",
    fontWeight: 600,
  },
};

const ImportsProcessPage = () => {
  const { importsUrl, importsSummaryUrl } = useGlobalContext();
  const { formatMessage: t } = useIntl();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { id: migrationJobTypeId } = useParams<{ id: string }>();

  useTitle(t({ id: "importsStep3Label" }));

  const { context, data, file, mappings, jobId } = importsUploadStore.useState(
    (s) => ({
      context: s.context,
      mappings: s.mappings,
      file: s.file,
      data: s.data,
      jobId: s.jobId,
    })
  );

  const {
    bulkUploadProgressStatus,
    shouldAbortUpload,
    uploadCompleted,
  } = importsBulkUploadStore.useState((s) => ({
    bulkUploadProgressStatus: s.bulkUploadProgressStatus,
    shouldAbortUpload: s.shouldAbortUpload,
    uploadCompleted: s.uploadCompleted,
  }));

  useEffect(() => {
    //if data or mappings are not present, means that the user refreshed the page, so needs to be redirected to the upload page.
    if (data == null || mappings == null) {
      navigate(importsUrl());
      return;
    }

    processFileImport(context!, file!, migrationJobTypeId!, mappings);
    queryClient.invalidateQueries("importJobSummaries");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (uploadCompleted) {
      navigate(importsSummaryUrl(jobId));
    }
  }, [importsSummaryUrl, navigate, jobId, migrationJobTypeId, uploadCompleted]);

  const execute = async () => {
    await processFileImport(context!, file!, migrationJobTypeId!, mappings);
  };

  return (
    <>
      <Typography variant="h4">
        <T id="importsStep3Label" />
      </Typography>
      <Box mb={2}></Box>
      <ImportsStepper activeStep={2} />
      <Grid container justifyContent="center">
        <Grid item xs={12} sm={12} md={12}>
          <Card sx={styles.root}>
            <CardContent>
              <Typography
                variant="h2"
                color="textSecondary"
                component="h2"
                sx={styles.title}
                gutterBottom
              >
                {t({ id: "importsProcessWarningMessage" })}
              </Typography>
              <Box mb={2}></Box>

              {!shouldAbortUpload ? (
                <>
                  {!uploadCompleted &&
                  Math.floor(bulkUploadProgressStatus!) < 100 ? (
                    <>
                      <Typography
                        variant="h5"
                        component="h2"
                        style={{ marginTop: 15, marginBottom: 15 }}
                      >
                        Progress: {Math.floor(bulkUploadProgressStatus!)} %
                      </Typography>
                      <CircularProgress size={32} />
                    </>
                  ) : (
                    <>
                      <div>
                        <Typography
                          variant="h5"
                          component="h5"
                          style={{
                            marginTop: 15,
                            marginBottom: 15,
                            color: Color.Green,
                            fontWeight: "bold",
                          }}
                        >
                          {t({ id: "importsUploadSuccessMessage" })}
                        </Typography>
                        <Typography
                          component="p"
                          style={{ marginTop: 15, marginBottom: 15 }}
                        >
                          {t({ id: "importsUploadSuccessNote" })}
                        </Typography>
                      </div>
                    </>
                  )}
                </>
              ) : (
                <>
                  <Typography
                    variant="h5"
                    component="h5"
                    style={{
                      marginTop: 15,
                      marginBottom: 15,
                      color: "orange",
                      fontWeight: "bold",
                    }}
                  >
                    {t({ id: "importsUploadAbortedMessage" })}
                  </Typography>
                </>
              )}

              <Box mb={2}></Box>
            </CardContent>
            <CardActions>
              {Math.floor(bulkUploadProgressStatus!) < 100 &&
                !shouldAbortUpload && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<CancelIcon />}
                    onClick={abortBulkImportsUpload}
                  >
                    {t({ id: "cancelButton" })}
                  </Button>
                )}
              {shouldAbortUpload && (
                <>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<AutorenewIcon />}
                    onClick={execute}
                  >
                    {t({ id: "restartButton" })}
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<BackupIcon />}
                    onClick={() => navigate(importsUrl())}
                  >
                    {t({ id: "reuploadButton" })}
                  </Button>
                </>
              )}
            </CardActions>
          </Card>
        </Grid>
        <Grid item xs={12} sm={12} md={12}>
          <BulkUploadProgress type="imports" />
        </Grid>
      </Grid>
    </>
  );
};

export default ImportsProcessPage;

